/**
 * CrewTab — Friends list management
 *
 * Displays all friends with online status. Online friends sorted to top.
 * Outgoing friend requests awaiting a response render in a PENDING section
 * above the friends, each with a cancel button.
 */

import { EventEmitter } from '../../../engine/core/EventEmitter';
import { FriendCell } from './FriendCell';
import type { FriendCellData } from './FriendCell';
import type { PendingFriendRequest } from '@sdl/shared';
import { formatStacks } from './formatStacks';

interface CrewTabEvents {
  'player:select': { playerId: string };
  'player:favourite': { playerId: string; isFavourite: boolean };
  'player:gift': { playerId: string };
  'pending:cancel': { playerId: string };
}

export class CrewTab extends EventEmitter<CrewTabEvents> {
  public element: HTMLElement;
  private pendingEl: HTMLElement;
  private listEl: HTMLElement;
  private emptyEl: HTMLElement;
  private cells: Map<string, FriendCell> = new Map();
  private friends: FriendCellData[] = [];
  private pending: PendingFriendRequest[] = [];

  constructor() {
    super();
    this.element = document.createElement('div');
    this.element.className = 'crew-tab-content';

    this.pendingEl = document.createElement('div');
    this.pendingEl.className = 'crew-pending-section';
    this.pendingEl.style.display = 'none';
    this.pendingEl.addEventListener('click', (e) => {
      const target = e.target as HTMLElement;
      const row = target.closest<HTMLElement>('.crew-pending-cell');
      if (!row) return;
      const playerId = row.dataset.playerId!;
      if (target.closest('.crew-pending-cancel')) {
        this.emit('pending:cancel', { playerId });
        return;
      }
      this.emit('player:select', { playerId });
    });

    this.listEl = document.createElement('div');
    this.listEl.className = 'crew-friend-list';

    this.emptyEl = document.createElement('div');
    this.emptyEl.className = 'crew-empty-state';
    this.emptyEl.innerHTML = `
      <div class="crew-empty-icon">👥</div>
      <div class="crew-empty-title">NO FRIENDS YET</div>
      <div class="crew-empty-text">Search for players to add to your crew</div>
    `;

    this.element.appendChild(this.pendingEl);
    this.element.appendChild(this.listEl);
    this.element.appendChild(this.emptyEl);
  }

  setFriends(friends: FriendCellData[]): void {
    this.friends = friends;
    this.renderList();
  }

  /** Replace the outgoing-requests section (rendered above the friends). */
  setPendingRequests(requests: PendingFriendRequest[]): void {
    this.pending = requests;
    this.renderPending();
    // Empty-state visibility depends on both lists.
    this.renderList();
  }

  updateOnlineStatus(playerId: string, online: boolean): void {
    const friend = this.friends.find(f => f.id === playerId);
    if (friend) {
      friend.isOnline = online;
      const cell = this.cells.get(playerId);
      if (cell) cell.update({ isOnline: online });
      // Re-sort: online friends first
      this.renderList();
    }
  }

  updateFavourite(playerId: string, isFavourite: boolean): void {
    const friend = this.friends.find(f => f.id === playerId);
    if (friend) {
      friend.isFavourite = isFavourite;
      const cell = this.cells.get(playerId);
      if (cell) cell.update({ isFavourite });
    }
  }

  /** Optimistic daily-gift send: cooldown state + coin flyout on the cell. */
  markGifted(playerId: string): void {
    const friend = this.friends.find(f => f.id === playerId);
    if (friend) friend.giftAvailable = false;
    this.cells.get(playerId)?.markGifted();
  }

  /** Re-arm (or disarm) a friend's gift button after a server verdict. */
  setGiftAvailable(playerId: string, available: boolean): void {
    const friend = this.friends.find(f => f.id === playerId);
    if (friend) friend.giftAvailable = available;
    this.cells.get(playerId)?.setGiftAvailable(available);
  }

  addFriend(friend: FriendCellData): void {
    if (this.friends.some(f => f.id === friend.id)) return;
    this.friends.push(friend);
    this.renderList();
  }

  removeFriend(playerId: string): void {
    this.friends = this.friends.filter(f => f.id !== playerId);
    this.renderList();
  }

  getFriends(): FriendCellData[] {
    return this.friends;
  }

  destroy(): void {
    this.cells.forEach(c => c.destroy());
    this.cells.clear();
    this.element.remove();
    this.removeAllListeners();
  }

  private renderPending(): void {
    if (this.pending.length === 0) {
      this.pendingEl.style.display = 'none';
      this.pendingEl.innerHTML = '';
      return;
    }

    const rows = this.pending.map((p) => {
      const initial = p.username.charAt(0).toUpperCase();
      const avatarContent = p.picture
        ? `<img src="${p.picture}" alt="${initial}" class="crew-cell-avatar-img">`
        : `<span class="crew-cell-avatar-initial">${initial}</span>`;
      return `
        <div class="crew-pending-cell" data-player-id="${p.playerId}">
          <div class="crew-cell-avatar">${avatarContent}</div>
          <div class="crew-cell-info">
            <div class="crew-cell-name">${p.username}</div>
            <div class="crew-cell-meta"><span class="crew-cell-stacks">${formatStacks(p.stacks ?? 0)}</span></div>
          </div>
          <span class="crew-pending-tag">PENDING</span>
          <button class="crew-pending-cancel" aria-label="Cancel request">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>`;
    }).join('');

    this.pendingEl.innerHTML = `
      <div class="crew-pending-header">PENDING REQUESTS</div>
      ${rows}
    `;
    this.pendingEl.style.display = 'flex';
  }

  private renderList(): void {
    // Clear existing cells
    this.cells.forEach(c => c.destroy());
    this.cells.clear();
    this.listEl.innerHTML = '';

    // Sort: online first, then alphabetical
    const sorted = [...this.friends].sort((a, b) => {
      if (a.isOnline !== b.isOnline) return a.isOnline ? -1 : 1;
      return a.username.localeCompare(b.username);
    });

    // Toggle empty state (pending requests count as content too)
    this.emptyEl.style.display = sorted.length === 0 && this.pending.length === 0 ? 'flex' : 'none';
    this.listEl.style.display = sorted.length === 0 ? 'none' : 'flex';

    for (const friend of sorted) {
      const cell = new FriendCell(friend);
      cell.on('select', (data) => this.emit('player:select', data));
      cell.on('favourite', (data) => this.emit('player:favourite', data));
      cell.on('gift', (data) => this.emit('player:gift', data));
      this.cells.set(friend.id, cell);
      this.listEl.appendChild(cell.element);
    }
  }
}
